import merge from 'lodash/merge';
import {THEMES} from './constants';
import {lightShadows, darkShadows} from './shadows';
import {Direction} from "@mui/system";
import {createTheme, responsiveFontSizes, Theme, ThemeOptions} from "@mui/material";

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

const MAIN_COLOR_LIGHT_THEME = '#1A2036'
const SECONDARY_COLOR_LIGHT_THEME = '#f4f5f7'

const MAIN_COLOR_DARK_THEME = '#f4f5f7'
const SECONDARY_COLOR_DARK_THEME = '#1A2036'


// Here is the place where I choose which color should I display when we are talking about DARK Theme or LIGHT Theme.

export const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: SECONDARY_COLOR_LIGHT_THEME,
            color: MAIN_COLOR_LIGHT_THEME
          }
        }

      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            width: '100% !important'
          }
        }
      },

      MuiStepper: {
        styleOverrides: {
          root: {
            '& .Mui-active': {
              color: '#1A2036 !important',
            },
            '& .Mui-completed': {
              color: '#1A2036 !important',
            },
          }
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(0,0,0,0)'
          }
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            fontWeight: 600,
          },
          containedPrimary: {
            backgroundColor: MAIN_COLOR_LIGHT_THEME,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#2c3856',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            color: MAIN_COLOR_LIGHT_THEME,
            backgroundImage: 'none',
          },
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: SECONDARY_COLOR_LIGHT_THEME,
            color: MAIN_COLOR_LIGHT_THEME,
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: MAIN_COLOR_LIGHT_THEME,

            '&&:before': {
              backgroundColor: MAIN_COLOR_LIGHT_THEME,
            }
          },
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: SECONDARY_COLOR_LIGHT_THEME,
            color: MAIN_COLOR_LIGHT_THEME,
          },
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: MAIN_COLOR_LIGHT_THEME,
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontWeight: 600,
          }
        }
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: MAIN_COLOR_LIGHT_THEME,
            },
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {

            '& .Mui-focused': {
              color: MAIN_COLOR_LIGHT_THEME,
            },

            '& label.Mui-focused': {
              color: MAIN_COLOR_LIGHT_THEME,
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: MAIN_COLOR_LIGHT_THEME,
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: MAIN_COLOR_LIGHT_THEME,
              },
              '&:hover fieldset': {
                borderColor: MAIN_COLOR_LIGHT_THEME,
              },
              '&.Mui-focused fieldset': {
                borderColor: MAIN_COLOR_LIGHT_THEME,
              },
            }
          }
        }
      }
    },
    palette: {
      action: {
        active: '#6b778c',
      },
      background: {
        default: '#f4f5f8',
        paper: '#ffffff',
      },
      error: {
        contrastText: '#ffffff',
        main: '#ef4444',
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: '#1a2035',
      },
      secondary: {
        contrastText: '#ffffff',
        main: '#6366f1',
      },
      success: {
        contrastText: '#ffffff',
        main: '#10b981',
      },
      text: {
        primary: '#1a2035',
        secondary: '#6b7280',
      },
      warning: {
        contrastText: '#ffffff',
        main: '#f59e0b',
      },
    },
    shadows: lightShadows,
  },
  [THEMES.DARK]: {
    components: {
      MuiBadge: {
        styleOverrides: {
          root: {
            backgroundColor: MAIN_COLOR_LIGHT_THEME,
            color: SECONDARY_COLOR_LIGHT_THEME,
          }
        }

      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            backgroundColor: MAIN_COLOR_DARK_THEME,
            color: SECONDARY_COLOR_DARK_THEME
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: SECONDARY_COLOR_DARK_THEME,
            color: MAIN_COLOR_DARK_THEME,
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: MAIN_COLOR_DARK_THEME,

            '&&:before': {
              backgroundColor: MAIN_COLOR_DARK_THEME,
            }
          }
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: SECONDARY_COLOR_DARK_THEME,
            color: MAIN_COLOR_LIGHT_THEME,
          },
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: MAIN_COLOR_LIGHT_THEME,
          }
        }
      }
    },
    palette: {
      background: {
        default: SECONDARY_COLOR_DARK_THEME,
        paper: '#222b36',
      },
      divider: 'rgba(145, 158, 171, 0.24)',
      error: {
        contrastText: '#ffffff',
        main: '#f44336',
      },
      mode: 'dark',
      primary: {
        contrastText: '#ffffff',
        main: '#688eff',
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50',
      },
      text: {
        primary: '#ffffff',
        secondary: '#919eab',
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800',
      },
    },
    shadows: darkShadows,
  },
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme || THEMES.DARK];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 16,
          },
        }),
      },
      {
        direction: config.direction,
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }
  return theme;
};

export const baseOptions: ThemeOptions = {
  direction: 'ltr',
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
  typography: {
    button: {
      fontWeight: 600,
    },
    fontFamily:
      '"Outfit", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    overline: {
      fontWeight: 600,
    },
  },
};
